import React from "react";
import { Chip, ChipProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Theme } from "@mui/material/styles";

const commonStyles = {
  border: 0,
  borderRadius: "var(--radius-round)",
  fontWeight: 700,
  fontSize: ".6rem",
  letterSpacing: 0,
  height: 18,
  minWidth: 65,
  paddingTop: 2,
};

type StatusChipStatus =
  | "error"
  | "success"
  | "info"
  | "upcoming renewal"
  | "hold"
  | "warn"
  | "arStatus"
  | "delivered"
  | "default"
  | string;

interface StatusStyleProps {
  status?: StatusChipStatus;
}

type StatusStyles = typeof commonStyles & {
  background?: string;
  color?: string;
};

const getStatusStyles = (
  status: StatusChipStatus | undefined,
  theme: Theme
): StatusStyles => {
  const baseStyles = { ...commonStyles };

  switch (status) {
    case "error":
      return {
        ...baseStyles,
        background: (theme.vars || theme)?.palette?.error?.light,
        color: (theme.vars || theme)?.palette?.error?.main,
      };
    case "success":
      return {
        ...baseStyles,
        background: (theme.vars || theme)?.palette?.success?.light,
        color: (theme.vars || theme)?.palette?.success?.main,
      };
    case "info":
      return {
        ...baseStyles,
        background: (theme.vars || theme)?.palette?.info?.light,
        color: (theme.vars || theme)?.palette?.info?.main,
      };
    case "upcoming renewal":
      return {
        ...baseStyles,
        background: (theme.vars || theme)?.palette?.primary?.light,
        color: (theme.vars || theme)?.palette?.primary?.main,
      };
    case "hold":
      return {
        ...baseStyles,
        background:
          "color-mix(in srgb, var(--neutral-600) 10.196078431372548%, transparent)",
        color: "var(--neutral-600)",
      };
    case "warn":
      return {
        ...baseStyles,
        background: (theme.vars || theme)?.palette?.warning?.light,
        color: (theme.vars || theme)?.palette?.warning?.main,
      };
    case "arStatus":
      return {
        ...baseStyles,
        background: (theme.vars || theme)?.palette?.primary?.tint,
        color: (theme.vars || theme)?.palette?.primary?.main,
      };
    case "delivered":
      return {
        ...baseStyles,
        background: (theme.vars || theme)?.palette?.info?.light,
        color: (theme.vars || theme)?.palette?.info?.main,
      };
    default:
      return baseStyles;
  }
};

const StyledChip = styled(Chip)<StatusStyleProps>(({ theme, status }) => ({
  ...getStatusStyles(status, theme),
}));

const StyledLabel = styled("span", {
  shouldForwardProp: (prop) => prop !== "status",
})<StatusStyleProps>(({ theme, status }) => {
  const styles = getStatusStyles(status, theme);

  return {
    ...styles,
    padding: "0px",
    background: "transparent",
    fontSize: "11px",
    color: `${styles.color} !important`,
  };
});

interface StatusChipProps extends ChipProps {
  status?: StatusChipStatus;
  showOnlyLabel?: boolean;
}

const StatusChip = ({
  label = "",
  status = "default",
  showOnlyLabel = false,
  ...props
}: StatusChipProps) => {
  if (showOnlyLabel) {
    return <StyledLabel status={status}>{label}</StyledLabel>;
  }

  return (
    <StyledChip
      size="medium"
      label={label}
      status={status}
      {...(props as ChipProps)}
    />
  );
};

export default StatusChip;
